// frontend/src/Components/SyncTo/hooks/useSyncToIDB.js
// Phase 30 — IndexedDB persistence layer for SyncTo recordings.
//
// DB: 'syncto-recordings' v1.
// Stores:
//   - chunks:   { id (auto), sessionKey, userId, seq, blob, mimeType, recordedAt }
//               indexes: sessionKey, userId
//   - sessions: { sessionKey (PK), userId, appointmentId, patientLabel,
//                 startedAt, mimeType, lastSeq }
//
// Invariants:
//   - appendChunk writes to BOTH stores in a single atomic tx (D-06).
//   - purgeBySessionKey deletes chunks + sessions row atomically (D-04 / SYNC-06).
//   - scanOrphans is userId-scoped (PITFALLS #6) + auto-deletes >24h rows (D-08).
//   - purgeAll clears both stores (logout path — PITFALLS #6).
//   - openRecordingDB is idempotent via a module-scope singleton promise.
//
// Source pattern: 30-RESEARCH.md Pattern 4 (verbatim for core ops).

import { openDB } from 'idb';
import { useCallback } from 'react';

const DB_NAME = 'syncto-recordings';
const DB_VERSION = 1;
const TTL_MS = 24 * 60 * 60 * 1000; // D-08 24h TTL for orphan rows

let _dbPromise = null;

export async function openRecordingDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion /*, newVersion, transaction, event */) {
      if (oldVersion < 1) {
        const chunks = db.createObjectStore('chunks', { keyPath: 'id', autoIncrement: true });
        chunks.createIndex('sessionKey', 'sessionKey');
        chunks.createIndex('userId', 'userId');

        db.createObjectStore('sessions', { keyPath: 'sessionKey' });
      }
    },
    blocked()    { /* another tab holds an older version open — no-op */ },
    blocking()   { /* this tab is blocking another upgrade — no-op */ },
    terminated() { _dbPromise = null; },
  });
  return _dbPromise;
}

/**
 * createSession — called at Start (D-06). lastSeq begins at -1; appendChunk bumps it.
 */
export async function createSession(row) {
  const db = await openRecordingDB();
  await db.put('sessions', row);
}

/**
 * appendChunk — atomic write of a new chunk AND monotonic bump of sessions.lastSeq.
 * Both writes succeed together or fail together (D-06 + T-30-07 mitigation).
 */
export async function appendChunk(row) {
  const db = await openRecordingDB();
  const tx = db.transaction(['chunks', 'sessions'], 'readwrite');
  const sessionsRow = await tx.objectStore('sessions').get(row.sessionKey);
  if (!sessionsRow) {
    throw new Error(`useSyncToIDB.appendChunk: sessions row missing for ${row.sessionKey}`);
  }
  await Promise.all([
    tx.objectStore('chunks').add(row),
    tx.objectStore('sessions').put({ ...sessionsRow, lastSeq: row.seq }),
    tx.done,
  ]);
}

/**
 * assembleBlob — concatenate all chunks for a sessionKey in seq order.
 * Returns a single Blob typed with the given mimeType.
 */
export async function assembleBlob(sessionKey, mimeType) {
  const db = await openRecordingDB();
  const rows = await db.getAllFromIndex('chunks', 'sessionKey', sessionKey);
  rows.sort((a, b) => a.seq - b.seq);
  return new Blob(rows.map((r) => r.blob), { type: mimeType });
}

/**
 * purgeBySessionKey — delete all chunks for sessionKey AND the sessions row,
 * atomically. Used on Cancel (SYNC-06), Stop-after-assemble (D-04), and
 * 24h-TTL auto-delete inside scanOrphans.
 */
export async function purgeBySessionKey(sessionKey) {
  const db = await openRecordingDB();
  const tx = db.transaction(['chunks', 'sessions'], 'readwrite');
  const chunkStore = tx.objectStore('chunks');
  const idx = chunkStore.index('sessionKey');
  for await (const cursor of idx.iterate(sessionKey)) {
    await cursor.delete();
  }
  await tx.objectStore('sessions').delete(sessionKey);
  await tx.done;
}

/**
 * purgeAll — wipe both stores. Called from BOTH logout handlers
 * (App.js and BootNav.jsx) — PITFALLS #6.
 */
export async function purgeAll() {
  const db = await openRecordingDB();
  const tx = db.transaction(['chunks', 'sessions'], 'readwrite');
  await Promise.all([
    tx.objectStore('chunks').clear(),
    tx.objectStore('sessions').clear(),
    tx.done,
  ]);
}

/**
 * scanOrphans — userId-scoped orphan list (PITFALLS #6). Rows older than 24h
 * are auto-deleted (D-08). Never autoloads — caller is responsible for the
 * surface prompt.
 *
 * Returns: array of sessions rows (userId match, not expired), sorted newest first.
 */
export async function scanOrphans(userId) {
  if (!userId) return [];
  const db = await openRecordingDB();
  const cutoff = Date.now() - TTL_MS;
  const all = await db.getAll('sessions');
  const orphans = [];
  for (const row of all) {
    if (row.userId !== userId) continue;
    if (row.startedAt < cutoff) {
      await purgeBySessionKey(row.sessionKey);
      continue;
    }
    orphans.push(row);
  }
  orphans.sort((a, b) => b.startedAt - a.startedAt);
  return orphans;
}

/**
 * Hook wrapper — stable-identity useCallbacks wrapping the module-level ops.
 * Use this inside components for DX; use the named exports directly in logout
 * handlers (non-React contexts) and inside RecordingProvider effects.
 */
export function useSyncToIDB() {
  return {
    createSession:     useCallback(createSession, []),
    appendChunk:       useCallback(appendChunk, []),
    assembleBlob:      useCallback(assembleBlob, []),
    purgeBySessionKey: useCallback(purgeBySessionKey, []),
    purgeAll:          useCallback(purgeAll, []),
    scanOrphans:       useCallback(scanOrphans, []),
  };
}
